import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import { EmptyState, CommonModal } from "../../components/Common";
import api, { apiErrorMessage } from "../../lib/api";
import { toast } from "sonner";
import { Search, Plus, Trash2, CheckCircle, XCircle, Users, Activity, FileText, Pill, Download, Filter } from "lucide-react";
import Edit from "lucide-react/dist/esm/icons/square-pen";
import { useCan } from "../../lib/permissions";

export function ClinicPatientsListPage({ links, homeBase = "/clinic-admin" }) {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [gender, setGender] = useState("");
  const [registeredFrom, setRegisteredFrom] = useState("");
  const [registeredTo, setRegisteredTo] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagIds, setTagIds] = useState([]);
  
  const [selectedIds, setSelectedIds] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const canCreate = useCan("patients.create");
  const canEdit = useCan("patients.edit");
  const canDelete = useCan("patients.delete");
  const canRestore = useCan("patients.restore");
  const canBulk = useCan("patients.bulk_actions");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/clinic/patients-x", { params: { q, status, gender, include_deleted: includeDeleted, tag_ids: tagIds.join(",") } });
      setPatients(res.data);
    } catch (e) {
      if (e?.code !== "ERR_CANCELED" && e?.name !== "CanceledError") toast.error("Failed to load patients");
    } finally {
      setLoading(false);
    }
  }, [q, status, gender, includeDeleted, tagIds]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/clinic/patients-x/tags").then(r => setTags(r.data)).catch(() => {}); }, []);
  const visiblePatients = patients.filter((patient) => {
    const day = String(patient.created_at || patient.registered_at || "").slice(0, 10);
    return (!registeredFrom || (day && day >= registeredFrom)) && (!registeredTo || (day && day <= registeredTo));
  });

  const handleSelect = (id) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(i => i !== id));
    else setSelectedIds([...selectedIds, id]);
  };
  
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds(visiblePatients.map(p => p.patient_id));
    else setSelectedIds([]);
  };

  const handleBulk = async (action) => {
    if (!selectedIds.length) return toast.error("Select patients first");
    setPendingAction({ type: "bulk", action, patientIds: [...selectedIds], title: `${action} selected patients`, message: `This will ${action} ${selectedIds.length} selected patient record${selectedIds.length === 1 ? "" : "s"}.` });
  };

  const executePendingAction = async () => {
    if (!pendingAction || actionBusy) return;
    setActionBusy(true);
    try {
      if (pendingAction.type === "bulk") {
        await api.post("/clinic/patients-x/bulk", { patient_ids: pendingAction.patientIds, action: pendingAction.action });
        toast.success(`Bulk ${pendingAction.action} successful`);
        setSelectedIds([]);
      } else if (pendingAction.type === "delete") {
        await api.delete(`/clinic/patients-x/${pendingAction.patientId}`);
        toast.success("Patient archived");
      } else {
        await api.post(`/clinic/patients-x/${pendingAction.patientId}/restore`);
        toast.success("Patient restored");
      }
      setPendingAction(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Patient action failed");
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = (id, name) => setPendingAction({ type: "delete", patientId: id, title: "Archive patient", message: `Archive ${name || "this patient"}? Their clinical history will be retained and the record can be restored.` });

  const handleRestore = (id, name) => setPendingAction({ type: "restore", patientId: id, title: "Restore patient", message: `Restore ${name || "this patient"} to the active patient directory?` });

  return (
    <DashboardLayout links={links} title="Patients Directory">
      <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute top-3 left-3 text-slate-400" />
            <input className="mg-input pl-9 w-64" placeholder="Search name, phone, email..." value={q} onChange={e=>setQ(e.target.value)} />
          </div>
          <select className="mg-input w-32" value={status} onChange={e=>setStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <select className="mg-input w-28" value={gender} onChange={e=>setGender(e.target.value)}>
            <option value="">Any Gender</option>
            <option>Male</option><option>Female</option><option>Other</option>
          </select>
          <label className="text-xs text-slate-500">Registered from<input type="date" className="mg-input block mt-1" value={registeredFrom} onChange={e=>setRegisteredFrom(e.target.value)} /></label>
          <label className="text-xs text-slate-500">Registered to<input type="date" className="mg-input block mt-1" value={registeredTo} onChange={e=>setRegisteredTo(e.target.value)} /></label>
          <select className="mg-input w-40" value="" onChange={e => e.target.value && setTagIds(ids => ids.includes(e.target.value) ? ids : [...ids, e.target.value])}>
            <option value="">Filter by tag…</option>
            {tags.filter(t => !tagIds.includes(t.tag_id)).map(t => <option key={t.tag_id} value={t.tag_id}>{t.display_name}</option>)}
          </select>
          {tagIds.map(id => {
            const tag = tags.find(t => t.tag_id === id);
            return tag ? <button key={id} onClick={() => setTagIds(tagIds.filter(x => x !== id))} className="text-xs px-2 py-1 rounded-full border" style={{borderColor: tag.color, color: tag.color}}>{tag.display_name} ×</button> : null;
          })}
          {canDelete && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={includeDeleted} onChange={e=>setIncludeDeleted(e.target.checked)} />
              Show Deleted
            </label>
          )}
        </div>
        
        <div className="flex gap-2">
          {selectedIds.length > 0 && canBulk && (
            <div className="flex gap-1 items-center bg-indigo-50 px-3 py-1 rounded-xl border border-indigo-100">
              <span className="text-xs font-semibold text-indigo-700 mr-2">{selectedIds.length} selected:</span>
              <button onClick={() => handleBulk('activate')} className="mg-btn-secondary text-xs py-1"><CheckCircle className="w-3 h-3"/> Activate</button>
              <button onClick={() => handleBulk('deactivate')} className="mg-btn-secondary text-xs py-1"><XCircle className="w-3 h-3"/> Deactivate</button>
              {canDelete && <button onClick={() => handleBulk('delete')} className="mg-btn-secondary text-xs py-1 text-red-600 hover:text-red-700 border-red-200"><Trash2 className="w-3 h-3"/> Delete</button>}
            </div>
          )}
          {canCreate && (
            <button onClick={() => navigate(`${homeBase}/patients/new`)} className="mg-btn-primary">
              <Plus className="w-4 h-4" /> New Patient
            </button>
          )}
        </div>
      </div>

      <div className="mg-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase text-slate-500 tracking-wider">
            <tr>
              {canBulk && <th className="p-4 w-10"><input type="checkbox" onChange={handleSelectAll} checked={visiblePatients.length > 0 && visiblePatients.every(patient => selectedIds.includes(patient.patient_id))} /></th>}
              <th className="py-4">Patient</th>
              <th>Contact</th>
              <th>Demographics</th>
              <th>Status</th>
              <th className="text-right pr-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">Loading...</td></tr>
            ) : visiblePatients.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center"><EmptyState title="No patients match these filters" /></td></tr>
            ) : (
              visiblePatients.map(p => (
                <tr key={p.patient_id} className={`hover:bg-slate-50 ${p.deleted_at ? 'opacity-60 bg-slate-50' : ''}`}>
                  {canBulk && <td className="p-4"><input type="checkbox" checked={selectedIds.includes(p.patient_id)} onChange={() => handleSelect(p.patient_id)} /></td>}
                  <td className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full mg-gradient-primary flex items-center justify-center text-white font-bold flex-shrink-0">
                        {p.name.charAt(0)}
                      </div>
                      <div>
                        <Link to={`${homeBase}/patients/${p.patient_id}`} className="font-bold text-mg-navy hover:text-mg-medical">{p.name}</Link>
                        <div className="text-xs text-slate-500 font-mono">ID: {p.patient_id.split('_')[1]}</div>
                        {p.tags?.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{p.tags.map(tag => <span key={tag.tag_id} className="text-[10px] px-1.5 py-0.5 rounded-full text-white" style={{backgroundColor: tag.color}}>{tag.display_name}</span>)}</div>}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="text-slate-700">{p.phone || "—"}</div>
                    <div className="text-xs text-slate-400">{p.email || "—"}</div>
                  </td>
                  <td>
                    <div className="text-slate-600">{p.gender || "—"} {p.age ? `• ${p.age}y` : ""}</div>
                    {p.blood_group && <div className="text-xs text-red-500 font-semibold">{p.blood_group}</div>}
                  </td>
                  <td>
                    {p.deleted_at ? (
                      <span className="mg-status mg-status-cancelled">Deleted</span>
                    ) : (
                      <span className={`mg-status mg-status-${p.status?.toLowerCase() || 'active'}`}>{p.status || "Active"}</span>
                    )}
                  </td>
                  <td className="text-right pr-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`${homeBase}/patients/${p.patient_id}`} className="mg-btn-secondary text-xs py-1">View 360°</Link>
                      {canDelete && !p.deleted_at && <button onClick={() => handleDelete(p.patient_id, p.name)} className="text-red-400 hover:text-red-600" aria-label={`Archive ${p.name}`}><Trash2 className="w-4 h-4" /></button>}
                      {canRestore && p.deleted_at && <button onClick={() => handleRestore(p.patient_id, p.name)} className="text-indigo-500 hover:text-indigo-700 font-semibold text-xs">Restore</button>}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pendingAction && <CommonModal title={pendingAction.title} onClose={() => !actionBusy && setPendingAction(null)} onSave={executePendingAction} saveText={actionBusy ? "Working…" : pendingAction.type === "delete" ? "Archive patient" : pendingAction.type === "restore" ? "Restore patient" : "Confirm action"}>
        <p className="text-sm text-slate-600">{pendingAction.message}</p>
      </CommonModal>}
    </DashboardLayout>
  );
}

export function ClinicPatientFormPage({ links, homeBase = "/clinic-admin" }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", gender: "", date_of_birth: "",
    blood_group: "", address: "", emergency_contact_name: "", emergency_contact_phone: "",
    status: "Active"
  });
  const [tagIds, setTagIds] = useState([]);

  const save = async () => {
    if (!form.name || !form.phone) return toast.error("Name and Phone are required");
    try {
      const res = await api.post("/clinic/patients-x", { ...form, email: form.email.trim() || null });
      if (tagIds.length) await api.put(`/clinic/patients-x/tag-assignments/${res.data.patient_id}`, { tag_ids: tagIds, source: "patient_create" });
      toast.success("Patient created");
      navigate(`${homeBase}/patients/${res.data.patient_id || res.data.id}`);
    } catch(e) {
      toast.error(apiErrorMessage(e, "Failed to create patient"));
    }
  };

  return (
    <DashboardLayout links={links} title="New Patient">
       <div className="max-w-2xl">
         <div className="mg-card space-y-4">
            <div className="grid grid-cols-2 gap-4">
               <div className="col-span-2">
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Full Name *</label>
                  <input className="mg-input" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} placeholder="John Doe" />
               </div>
               <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Phone *</label>
                  <input className="mg-input" value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} placeholder="+919876543210" />
               </div>
               <div className="col-span-2">
                 <label className="text-sm font-semibold text-slate-700 mb-1 block">Patient Tags</label>
                 <PatientTagPicker value={tagIds} onChange={setTagIds} />
               </div>
               <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Email</label>
                  <input type="email" className="mg-input" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} placeholder="john@example.com" />
               </div>
               <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Gender</label>
                  <select className="mg-input" value={form.gender} onChange={e=>setForm({...form, gender: e.target.value})}>
                     <option value="">Select</option>
                     <option>Male</option><option>Female</option><option>Other</option>
                  </select>
               </div>
               <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Date of Birth</label>
                  <input type="date" className="mg-input" value={form.date_of_birth} onChange={e=>setForm({...form, date_of_birth: e.target.value})} />
               </div>
               <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Blood Group</label>
                  <input className="mg-input" value={form.blood_group} onChange={e=>setForm({...form, blood_group: e.target.value})} placeholder="e.g. O+" />
               </div>
               <div className="col-span-2">
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Address</label>
                  <input className="mg-input" value={form.address} onChange={e=>setForm({...form, address: e.target.value})} />
               </div>
               <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Emergency Contact Name</label>
                  <input className="mg-input" value={form.emergency_contact_name} onChange={e=>setForm({...form, emergency_contact_name: e.target.value})} />
               </div>
               <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Emergency Phone</label>
                  <input className="mg-input" value={form.emergency_contact_phone} onChange={e=>setForm({...form, emergency_contact_phone: e.target.value})} />
               </div>
            </div>
            
            <div className="pt-6 border-t border-slate-100 flex gap-3">
              <button onClick={() => navigate(-1)} className="mg-btn-secondary px-8">Cancel</button>
              <button onClick={save} className="mg-btn-primary px-8">Create Patient</button>
            </div>
         </div>
       </div>
    </DashboardLayout>
  );
}

export function PatientTagPicker({ value = [], onChange }) {
  const [tags, setTags] = useState([]);
  const [newName, setNewName] = useState("");
  const loadTags = () => api.get("/clinic/patients-x/tags").then(r => setTags(r.data)).catch(() => toast.error("Patient tags could not be loaded"));
  useEffect(() => { loadTags(); }, []);
  const createTag = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/clinic/patients-x/tags", { name: newName.trim(), color: "#2563eb" });
      setTags(current => current.some(t => t.tag_id === data.tag_id) ? current : [...current, data]);
      onChange([...new Set([...value, data.tag_id])]);
      setNewName("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create tag"); }
  };
  return <div className="space-y-2">
    <div className="flex flex-wrap gap-2">{tags.map(tag => <button type="button" key={tag.tag_id} onClick={() => onChange(value.includes(tag.tag_id) ? value.filter(id => id !== tag.tag_id) : [...value, tag.tag_id])} className={`text-xs px-2.5 py-1 rounded-full border ${value.includes(tag.tag_id) ? "text-white" : "bg-white"}`} style={{borderColor: tag.color, backgroundColor: value.includes(tag.tag_id) ? tag.color : undefined, color: value.includes(tag.tag_id) ? "white" : tag.color}}>{tag.display_name}</button>)}</div>
    <div className="flex gap-2"><input className="mg-input" maxLength={50} value={newName} onChange={e => setNewName(e.target.value)} placeholder="Create a reusable tag" onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); createTag(); } }} /><button type="button" onClick={createTag} className="mg-btn-secondary whitespace-nowrap">Add tag</button></div>
  </div>;
}
